import type { RoundCountSummary } from "@cloudnest/horror-race-engine";
import { SUMMARY_CHARACTER_ORDER } from "@cloudnest/horror-race-engine";

interface CoinMemoryExactRowProps {
  /** The "ground truth" summary derived from the latest complete round.
   *  Caller passes `null` from `selectExactSummary(state)`; in that case the
   *  component itself returns null (no DOM). */
  readonly summary: RoundCountSummary | null;
}

const EXACT_CELL_STYLE: React.CSSProperties = {
  padding: "6px 4px",
  textAlign: "center",
  fontFamily: "ui-monospace, monospace",
  color: "var(--cn-horror-text)",
  fontWeight: 700,
  background: "color-mix(in srgb, var(--cn-horror-p3) 8%, transparent)",
  borderBottom:
    "1px solid color-mix(in srgb, var(--cn-horror-p3) 35%, transparent)",
};
const EXACT_LBL_STYLE: React.CSSProperties = {
  ...EXACT_CELL_STYLE,
  textAlign: "left",
  paddingLeft: "10px",
  fontWeight: 800,
  whiteSpace: "nowrap",
};
const STAR_STYLE: React.CSSProperties = {
  color: "var(--cn-horror-finished-flash)",
  fontSize: "12px",
  lineHeight: 1,
};

/**
 * M1 — "★" exact summary row at the top of the coin-memory sheet. Renders
 * iff `summary` is non-null (i.e. at least one complete round exists in the
 * current race). Single-line label = ★ glyph only (no source-round number;
 * the row's value is "ground truth exists", not "from round N"). Light
 * mauve fill + gold star is the only emphasis the sheet carries.
 *
 * The star is a decorative glyph on a non-interactive row; it is NOT a
 * clickability signal, so the project no-icon-for-clickability rule does
 * not apply.
 */
export function CoinMemoryExactRow({ summary }: CoinMemoryExactRowProps) {
  if (summary === null) return null;
  return (
    <tr data-testid="exact-summary-row">
      <td
        style={EXACT_LBL_STYLE}
        title="완성 라운드 기준 정확 카운트 (포기 없는 라운드)"
      >
        <span style={STAR_STYLE}>★</span>
      </td>
      {SUMMARY_CHARACTER_ORDER.map((char) => (
        <td key={char} style={EXACT_CELL_STYLE}>
          {summary.counts[char]}
        </td>
      ))}
      <td
        style={{
          ...EXACT_CELL_STYLE,
          color:
            summary.unknownCount === 0
              ? "var(--cn-horror-text-muted)"
              : "var(--cn-horror-error)",
          opacity: summary.unknownCount === 0 ? 0.5 : 1,
        }}
      >
        {summary.unknownCount}
      </td>
    </tr>
  );
}
